import { useEffect, useMemo, useState } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme, useMediaQuery, Drawer, Stack, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { useSelector } from "react-redux";

import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import LocalTaxiOutlinedIcon from "@mui/icons-material/LocalTaxiOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { getStoredUserType } from "../../services/authService";
import { getMyProfile } from "../../services/userService";

const Item = ({ title, to, icon, selected, setSelected, onNavigate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => {
        setSelected(title);
        if (onNavigate) onNavigate();
      }}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const ADMIN_ROLES = ["Boss", "LicenseUser"];
const DRIVER_ALLOWED = ["Boss", "Driver", "LicenseUser"];

const Sidebar = ({ isSidebar = true, setIsSidebar }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery("(max-width:900px)");
  const [isCollapsedDesktop, setIsCollapsedDesktop] = useState(false);
  const [selected, setSelected] = useState("Dashboard");
  const { type: userTypeFromState, driverName: driverNameFromState } =
    useSelector((state) => state.user) || {};
  const storedUserData = useMemo(() => {
    try {
      const saved = localStorage.getItem("userData");
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.warn("[Sidebar] No se pudo leer userData", err);
      return {};
    }
  }, []);
  const userType =
    userTypeFromState || storedUserData.type || getStoredUserType();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchProfile = async () => {
      try {
        const data = await getMyProfile();
        if (active) setProfile(data);
      } catch (err) {
        console.warn("[Sidebar] No se pudo obtener el perfil:", err);
      }
    };
    if (userType) {
      fetchProfile();
    }
    return () => {
      active = false;
    };
  }, [userType]);

  const fullName = useMemo(() => {
    if (!profile) return "";
    const name = [profile.name, profile.lastName].filter(Boolean).join(" ").trim();
    return name || profile.username || "";
  }, [profile]);

  const displayName =
    fullName ||
    driverNameFromState ||
    storedUserData.driverName ||
    "Usuario";

  const roleLabel = useMemo(() => {
    switch (userType) {
      case "Boss":
        return "Jefe";
      case "Driver":
        return "Conductor";
      case "LicenseUser":
        return "Usuario de licencia";
      default:
        return "Usuario";
    }
  }, [userType]);

  const isItemVisible = (roles) => !roles || roles.includes(userType);

  const primaryHeader =
    userType === "Driver" ? "CONDUCTOR" : "ADMINISTRACIÓN";

  const adminItems = useMemo(
    () => [
      {
        title: "Panel de Control",
        to: "/dashboard",
        icon: <HomeOutlinedIcon />,
        roles: ADMIN_ROLES,
      },
      {
        title: "Administrar Conductores",
        to: "/drivers",
        icon: <PeopleOutlinedIcon />,
        roles: ADMIN_ROLES,
      },
      {
        title: "Licencias",
        to: "/licenses",
        icon: <LocalTaxiOutlinedIcon />,
        roles: ADMIN_ROLES,
      },
      {
        title: "Vehículos",
        to: "/vehicles",
        icon: <LocalTaxiOutlinedIcon />,
        roles: ADMIN_ROLES,
      },
    ],
    []
  );

  const operativaItems = useMemo(
    () => [
      {
        title: "Hojas de Turnos",
        to: "/turnos",
        icon: <ContactsOutlinedIcon />,
        roles: DRIVER_ALLOWED,
      },
      {
        title: "Liquidaciones",
        to: "/balanceSheets",
        icon: <ReceiptOutlinedIcon />,
        roles: DRIVER_ALLOWED,
      },
      {
        title: "Turnos Liquidados",
        to: "/turnosliquidados",
        icon: <ContactsOutlinedIcon />,
        roles: DRIVER_ALLOWED,
      },
      {
        title: "Servicios Abonados",
        to: "/servicios-abonados",
        icon: <ReceiptOutlinedIcon />,
        roles: DRIVER_ALLOWED,
      },
    ],
    []
  );

  const personalItems = useMemo(
    () => [
      {
        title: "Mi Usuario",
        to: "/user",
        icon: <PersonOutlinedIcon />,
        roles: DRIVER_ALLOWED,
      },
      {
        title: "Notificaciones",
        to: "/notifications",
        icon: <NotificationsOutlinedIcon />,
        roles: DRIVER_ALLOWED,
      },
      {
        title: "Ayuda",
        to: "/help",
        icon: <HelpOutlineOutlinedIcon />,
        roles: DRIVER_ALLOWED,
      },
    ],
    []
  );

  const analyticsItems = useMemo(
    () => [
      {
        title: "Evolución",
        to: "/analytics",
        icon: <TimelineOutlinedIcon />,
        roles: ADMIN_ROLES,
      },
      {
        title: "Mapa de calor",
        to: "/heatmap",
        icon: <TimelineOutlinedIcon />,
        roles: ADMIN_ROLES,
      },
    ],
    []
  );

  const renderItems = (items) =>
    items
      .filter(({ roles }) => isItemVisible(roles))
      .map(({ title, to, icon }) => (
        <Item
          key={title}
          title={title}
          to={to}
          icon={icon}
          selected={selected}
          setSelected={setSelected}
          onNavigate={() => {
            if (isMobile && setIsSidebar) setIsSidebar(false);
          }}
        />
      ));

  const hasVisible = (items) =>
    items.some(({ roles }) => isItemVisible(roles));

  const sidebarContent = (
    <ProSidebar
      collapsed={isMobile ? false : isCollapsedDesktop}
      breakPoint="md"
      toggled={isMobile ? isSidebar : true}
    >
      <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => {
              if (isMobile && setIsSidebar) {
                setIsSidebar(false);
              } else {
                setIsCollapsedDesktop((prev) => !prev);
              }
            }}
            icon={!isMobile && isCollapsedDesktop ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isMobile && !isCollapsedDesktop && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h3" color={colors.grey[100]}>
                  {primaryHeader}
                </Typography>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCollapsedDesktop((prev) => !prev);
                  }}
                >
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsedDesktop && !isMobile && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width="100px"
                  height="100px"
                  src={`../../assets/user.png`}
                  style={{ cursor: "pointer", borderRadius: "50%" }}
                />
              </Box>
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  {displayName}
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]}>
                  {roleLabel}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsedDesktop ? undefined : "10%"}>
            {renderItems(adminItems)}

            {hasVisible(operativaItems) && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Operativa
                </Typography>
                {renderItems(operativaItems)}
              </>
            )}

            {hasVisible(personalItems) && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Cuenta
                </Typography>
                {renderItems(personalItems)}
              </>
            )}

            {hasVisible(analyticsItems) && (
              <>
                <Typography
                  variant="h6"
                  color={colors.grey[300]}
                  sx={{ m: "15px 0 5px 20px" }}
                >
                  Estadísticas
                </Typography>
                {renderItems(analyticsItems)}
              </>
            )}
          </Box>
      </Menu>
    </ProSidebar>
  );

  const sharedSx = {
    "& .pro-sidebar-inner": {
      background: `${colors.primary[400]} !important`,
      borderRight: isMobile ? `1px solid ${colors.grey[700]}` : "none",
    },
    "& .pro-icon-wrapper": {
      backgroundColor: "transparent !important",
    },
    "& .pro-inner-item": {
      padding: isMobile ? "8px 16px !important" : "5px 35px 5px 20px !important",
    },
    "& .pro-inner-item:hover": {
      color: "#868dfb !important",
    },
    "& .pro-menu-item.active": {
      color: "#6870fa !important",
    },
  };

  if (isMobile) {
    const sections = [
      { label: null, items: adminItems },
      { label: "Operativa", items: operativaItems },
      { label: "Cuenta", items: personalItems },
      { label: "Estadísticas", items: analyticsItems },
    ];

    const renderMobileItems = (items) =>
      items
        .filter(({ roles }) => isItemVisible(roles))
        .map(({ title, to, icon }) => (
          <Button
            key={title}
            startIcon={icon}
            onClick={() => {
              setSelected(title);
              setIsSidebar && setIsSidebar(false);
            }}
            component={Link}
            to={to}
            sx={{
              justifyContent: "flex-start",
              color: colors.grey[100],
              textTransform: "none",
              fontWeight: 700,
              py: 1.25,
              fontSize: "1rem",
              "& .MuiButton-startIcon": {
                minWidth: 28,
                "& svg": { fontSize: 22 },
              },
            }}
          >
            {title}
          </Button>
        ));

    return (
      <Drawer
        anchor="left"
        open={isSidebar}
        onClose={() => setIsSidebar && setIsSidebar(false)}
        PaperProps={{
          sx: {
            width: 260,
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              backgroundColor: colors.primary[400],
            }}
          >
            <Typography variant="h6" sx={{ color: colors.grey[100], fontWeight: 800 }}>
              {primaryHeader}
            </Typography>
            <IconButton
              aria-label="Cerrar menú"
              onClick={() => setIsSidebar && setIsSidebar(false)}
              sx={{ color: colors.grey[100] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              backgroundColor: colors.primary[400],
            }}
          >
            {sections.map(
              ({ label, items }) =>
                hasVisible(items) && (
                  <Box key={label || "main"}>
                    {label && (
                      <Typography variant="subtitle2" sx={{ color: colors.grey[300], mb: 0.5 }}>
                        {label}
                      </Typography>
                    )}
                    <Stack direction="column" spacing={0.5}>
                      {renderMobileItems(items)}
                    </Stack>
                  </Box>
                )
            )}
          </Box>
        </Box>
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        ...sharedSx,
        position: "relative",
        height: "100%",
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default Sidebar;
